import { z } from 'zod';
import { CognitoUserRoleSchema } from './cognito-user.schema';

// ── Row-level validation status ───────────────────────────────────────────────

export const BulkUserRowStatusSchema = z.enum([
  'VALID',
  'DUPLICATE_EMAIL',
  'INVALID_ROLE',
  'MISSING_FIELD',
]);
export type BulkUserRowStatus = z.infer<typeof BulkUserRowStatusSchema>;

// ── Single CSV row (validate & import) ────────────────────────────────────────

export const BulkUserRowSchema = z.object({
  email: z.string().min(1, 'Email is required'),
  given_name: z.string().min(1, 'Given name is required'),
  family_name: z.string().min(1, 'Family name is required'),
  role_code: CognitoUserRoleSchema.optional(),
  job_title: z.string().max(255).optional(),
  department: z.string().max(255).optional(),
  employee_number: z.string().max(100).optional(),
});
export type BulkUserRow = z.infer<typeof BulkUserRowSchema>;

// ── Validate request / response ───────────────────────────────────────────────

export const BulkUserValidateRequestSchema = z.object({
  users: z.array(BulkUserRowSchema).min(1, 'At least one user row is required').max(500, 'Maximum 500 rows per request'),
});
export type BulkUserValidateRequest = z.infer<typeof BulkUserValidateRequestSchema>;

export const BulkUserValidateRowResultSchema = z.object({
  row: z.number().int(),
  email: z.string(),
  given_name: z.string(),
  family_name: z.string(),
  role_code: CognitoUserRoleSchema,
  status: BulkUserRowStatusSchema,
  message: z.string().optional(),
});
export type BulkUserValidateRowResult = z.infer<typeof BulkUserValidateRowResultSchema>;

export const BulkUserValidateResponseSchema = z.object({
  rows: z.array(BulkUserValidateRowResultSchema),
  summary: z.object({
    total: z.number().int(),
    valid: z.number().int(),
    invalid: z.number().int(),
  }),
});
export type BulkUserValidateResponse = z.infer<typeof BulkUserValidateResponseSchema>;

// ── Import request / response ─────────────────────────────────────────────────

export const BulkUserImportRequestSchema = z.object({
  users: z.array(BulkUserRowSchema).min(1, 'At least one user is required').max(500, 'Maximum 500 users per request'),
});
export type BulkUserImportRequest = z.infer<typeof BulkUserImportRequestSchema>;

export const BulkUserImportRowResultSchema = z.object({
  row: z.number().int(),
  email: z.string(),
  status: z.enum(['CREATED', 'FAILED']),
  sub: z.string().optional(),
  message: z.string().optional(),
});
export type BulkUserImportRowResult = z.infer<typeof BulkUserImportRowResultSchema>;

export const BulkUserImportResponseSchema = z.object({
  results: z.array(BulkUserImportRowResultSchema),
  summary: z.object({
    total: z.number().int(),
    created: z.number().int(),
    failed: z.number().int(),
  }),
});
export type BulkUserImportResponse = z.infer<typeof BulkUserImportResponseSchema>;
